import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { folderAPI, fileAPI, shareAPI } from '../services/api';
import {
  FolderPlus,
  FilePlus,
  Folder,
  File,
  LogOut,
  Code2,
  Share2,
  Trash2,
  Copy,
  ChevronRight,
  ChevronDown,
  User,
} from 'lucide-react';
import '../Components/css/Dashboard.css';

const getFileIcon = (extension) => {
  const iconMap = {
    '.py': '🐍',
    '.js': '📜',
    '.java': '☕',
    '.cpp': '⚙️',
    '.c': '©️',
    '.txt': '📄',
  };
  return iconMap[extension] || '📄';
};

const getCompilerRoute = (extension) => {
  const routeMap = {
    '.py': '/compile/python',
    '.js': '/compile/js',
    '.java': '/compile/java',
    '.cpp': '/compile/cpp',
    '.c': '/compile/c',
    '.txt': '/editor/text',
  };
  return routeMap[extension] || null;
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showNewFileModal, setShowNewFileModal] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newFileExtension, setNewFileExtension] = useState('.py');
  const [parentFolderId, setParentFolderId] = useState(null);
  const [shareLink, setShareLink] = useState(null);

  useEffect(() => {
    loadFolders();
  }, []);

  const loadFolders = async () => {
    try {
      setLoading(true);
      const data = await folderAPI.getRootFolders();
      setFolders(data);
    } catch (error) {
      console.error('Error loading folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFolderContents = async (folderId) => {
    try {
      const folderData = await folderAPI.getFolder(folderId);
      updateFolderInTree(folderId, folderData);
    } catch (error) {
      console.error('Error loading folder contents:', error);
    }
  };

  const updateFolderInTree = (folderId, folderData) => {
    const updateFolder = (items) => {
      return items.map((item) => {
        if (item.id === folderId) {
          return { ...item, ...folderData };
        }
        if (item.subfolders) {
          return { ...item, subfolders: updateFolder(item.subfolders) };
        }
        return item;
      });
    };
    setFolders(updateFolder(folders));
  };

  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
      loadFolderContents(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleCreateFolder = async () => {
    if (!newItemName.trim()) return;

    try {
      if (parentFolderId) {
        await folderAPI.createSubfolder(parentFolderId, newItemName);
        loadFolderContents(parentFolderId);
      } else {
        await folderAPI.createRootFolder(newItemName);
        loadFolders();
      }
      setShowNewFolderModal(false);
      setNewItemName('');
      setParentFolderId(null);
    } catch (error) {
      console.error('Error creating folder:', error);
      alert('Failed to create folder');
    }
  };

  const handleCreateFile = async () => {
    if (!newItemName.trim()) return;

    try {
      const fileName = newItemName.endsWith(newFileExtension)
        ? newItemName
        : newItemName + newFileExtension;

      await fileAPI.createFile(fileName, newFileExtension, '', parentFolderId);

      if (parentFolderId) {
        loadFolderContents(parentFolderId);
      } else {
        loadFolders();
      }

      setShowNewFileModal(false);
      setNewItemName('');
      setParentFolderId(null);
    } catch (error) {
      console.error('Error creating file:', error);
      alert('Failed to create file');
    }
  };

  const handleOpenFile = (file) => {
    const route = getCompilerRoute(file.extension);
    if (route) {
      // For C and C++ files, pass fileId as query parameter for iframe-based compilers
      if (file.extension === '.cpp' || file.extension === '.c') {
        navigate(`${route}?fileId=${file.id}`);
      } else {
        // For other files, use state to pass file object
        navigate(route, { state: { file } });
      }
    } else {
      alert('No compiler available for this file type');
    }
  };

  const handleShareFile = async (file) => {
    try {
      const shareData = await shareAPI.createShareLink(file.id);
      const fullUrl = `${window.location.origin}/share/${shareData.token}`;
      setShareLink(fullUrl);
      setSelectedItem(file);
    } catch (error) {
      console.error('Error creating share link:', error);
      alert('Failed to create share link');
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    alert('Share link copied to clipboard!');
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await fileAPI.deleteFile(fileId);
      loadFolders();
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Are you sure you want to delete this folder and all its contents?')) return;

    try {
      await folderAPI.deleteFolder(folderId);
      loadFolders();
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert('Failed to delete folder');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderFolder = (folder, level = 0) => {
    const isExpanded = expandedFolders.has(folder.id);

    return (
      <div key={folder.id} className="folder-item">
        <div
          className="folder-header"
          style={{ paddingLeft: `${level * 20 + 10}px` }}
          onClick={() => toggleFolder(folder.id)}
        >
          <span className="folder-chevron">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
          <Folder size={18} className="folder-icon" />
          <span className="folder-name">{folder.name}</span>
          <div className="folder-actions">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setParentFolderId(folder.id);
                setShowNewFolderModal(true);
              }}
              title="New folder"
            >
              <FolderPlus size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setParentFolderId(folder.id);
                setShowNewFileModal(true);
              }}
              title="New file"
            >
              <FilePlus size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteFolder(folder.id);
              }}
              title="Delete folder"
              className="delete-btn"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="folder-contents">
            {folder.subfolders?.map((subfolder) => renderFolder(subfolder, level + 1))}
            {folder.files?.map((file) => (
              <div
                key={file.id}
                className="file-item"
                style={{ paddingLeft: `${(level + 1) * 20 + 26}px` }}
              >
                <span className="file-icon">{getFileIcon(file.extension)}</span>
                <span className="file-name" onClick={() => handleOpenFile(file)}>
                  {file.name}
                </span>
                <div className="file-actions">
                  <button onClick={() => handleOpenFile(file)} title="Open file">
                    <Code2 size={16} />
                  </button>
                  <button onClick={() => handleShareFile(file)} title="Share file">
                    <Share2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteFile(file.id)}
                    title="Delete file"
                    className="delete-btn"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
<div
  className="dashboard-container"
  style={{ width: "calc(100vw - 4rem)", marginLeft: "4rem" }}
>

      <div className="dashboard-sidebar">
        <div className="dashboard-header">
          <div className="dashboard-logo">
            <img src="/codii_logo_trans.png" alt="Codii" style={{width:100,height:"auto"}}/>
          </div>
          <div className="user-info">
            <User size={20} />
            <span>{user?.username}</span>
          </div>
        </div>

        <div className="dashboard-actions">
          <button
            className="action-btn primary"
            onClick={() => {
              setParentFolderId(null);
              setShowNewFolderModal(true);
            }}
          >
            <FolderPlus size={18} />
            New Folder
          </button>
          <button
            className="action-btn primary"
            onClick={() => {
              setParentFolderId(null);
              setShowNewFileModal(true);
            }}
          >
            <FilePlus size={18} />
            New File
          </button>
        </div>

        <div className="file-explorer">
          {loading ? (
            <div className="loading-state">Loading...</div>
          ) : folders.length === 0 ? (
            <div className="empty-state">
              <p>No files or folders yet</p>
              <p>Create your first folder or file to get started</p>
            </div>
          ) : (
            folders.map((folder) => renderFolder(folder))
          )}
        </div>

        <button className="logout-btn" onClick={handleLogout}>
          <LogOut size={18} />
          Logout
        </button>
      </div>

      <div className="dashboard-main">
        <div className="welcome-section">
          <h1>Welcome back, {user?.username}!</h1>
          <p>Select a file to start coding or create a new project</p>
        </div>

        <div className="quick-actions">
          <h2>Quick Start</h2>
          <div className="compiler-cards">
            <div className="compiler-card" onClick={() => navigate('/compile/python')}>
              <span className="card-icon">🐍</span>
              <h3>Python</h3>
              <p>Start coding in Python</p>
            </div>
            <div className="compiler-card" onClick={() => navigate('/compile/js')}>
              <span className="card-icon">📜</span>
              <h3>JavaScript</h3>
              <p>Start coding in JavaScript</p>
            </div>
            <div className="compiler-card" onClick={() => navigate('/compile/java')}>
              <span className="card-icon">☕</span>
              <h3>Java</h3>
              <p>Start coding in Java</p>
            </div>
            <div className="compiler-card" onClick={() => navigate('/compile/cpp')}>
              <span className="card-icon">⚙️</span>
              <h3>C++</h3>
              <p>Start coding in C++</p>
            </div>
            <div className="compiler-card" onClick={() => navigate('/compile/c')}>
              <span className="card-icon">©️</span>
              <h3>C</h3>
              <p>Start coding in C</p>
            </div>
          </div>
        </div>
      </div>

      {showNewFolderModal && (
        <div className="modal-overlay" onClick={() => setShowNewFolderModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Folder</h2>
            <input
              type="text"
              placeholder="Folder name"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
              autoFocus
            />
            <div className="modal-actions">
              <button onClick={() => setShowNewFolderModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleCreateFolder} className="create-btn">
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewFileModal && (
        <div className="modal-overlay" onClick={() => setShowNewFileModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Create New File</h2>
            <input
              type="text"
              placeholder="File name"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateFile()}
              autoFocus
            />
            <select
              value={newFileExtension}
              onChange={(e) => setNewFileExtension(e.target.value)}
            >
              <option value=".py">Python (.py)</option>
              <option value=".js">JavaScript (.js)</option>
              <option value=".java">Java (.java)</option>
              <option value=".cpp">C++ (.cpp)</option>
              <option value=".c">C (.c)</option>
              <option value=".txt">Text (.txt)</option>
            </select>
            <div className="modal-actions">
              <button onClick={() => setShowNewFileModal(false)} className="cancel-btn">
                Cancel
              </button>
              <button onClick={handleCreateFile} className="create-btn">
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {shareLink && (
        <div className="modal-overlay" onClick={() => setShareLink(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Share Link Generated</h2>
            <p>Anyone with this link can view this file:</p>
            <div className="share-link-container">
              <input type="text" value={shareLink} readOnly />
              <button onClick={copyShareLink} className="copy-btn">
                <Copy size={18} />
                Copy
              </button>
            </div>
            <div className="modal-actions">
              <button onClick={() => setShareLink(null)} className="create-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
